import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { AccountBalanceOutlined, ChevronRightRounded, VisibilityOffOutlined, VisibilityOutlined, ExpandMoreRounded } from "@mui/icons-material";
import { getMyBranchCash } from "api/branchCash";
import { user as getUser } from "localStorage";
import BranchCashKpis from "./BranchCashKpis";

// Per-user, per-device preference (not synced to backend — see feature discussion):
// which stations this staff member has collapsed out of their own Branch Cash list.
// Scoped by user id so a shared device doesn't leak one staff's hidden branches to
// the next person who logs in on it.
const storageKey = (userId) => `branchCash.hiddenStations.${userId || "anon"}`;
function loadHiddenStations(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function saveHiddenStations(userId, ids) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(ids));
  } catch {
    // localStorage unavailable (private mode/quota) — hiding just won't persist.
  }
}

const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const money = (n, c = "THB") => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const initials = (s) => (s || "?").slice(0, 2).toUpperCase();

const CARD_SHADOW = "0 1px 2px rgba(20,18,30,.04),0 16px 32px -24px rgba(20,18,30,.18)";
const labelSx = { fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#a8a3b4" };

export default function BranchCash() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [balances, setBalances] = useState([]);
  const [rate, setRate] = useState(null);
  const currentUser = getUser();
  const userId = currentUser?._id || currentUser?.id;
  const [hiddenIds, setHiddenIds] = useState(() => loadHiddenStations(userId));
  const [showHidden, setShowHidden] = useState(false);

  const load = useCallback(async () => {
    const res = await getMyBranchCash({ limit: 1 });
    if (res && res.success) {
      setBalances(res.balances || []);
      setRate(res.rate || null);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toTHB = (b) => {
    const lak = rate && rate.LAK ? (b.LAK || 0) / Number(rate.LAK) : 0;
    const usd = rate && rate.USD ? (b.USD || 0) * Number(rate.USD) : 0;
    return (b.THB || 0) + lak + usd;
  };

  const toggleHidden = (stationId, e) => {
    e.stopPropagation();
    setHiddenIds((prev) => {
      const next = prev.includes(stationId) ? prev.filter((id) => id !== stationId) : [...prev, stationId];
      saveHiddenStations(userId, next);
      return next;
    });
  };

  const visibleBalances = useMemo(() => balances.filter((b) => !hiddenIds.includes(b.stationId)), [balances, hiddenIds]);
  const hiddenBalances = useMemo(() => balances.filter((b) => hiddenIds.includes(b.stationId)), [balances, hiddenIds]);

  const renderCard = (b, hidden) => (
    <Box key={b.stationId} onClick={() => navigate(`/branch-cash/${encodeURIComponent(b.stationId)}`)}
      sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "20px", p: "20px 22px", boxShadow: CARD_SHADOW, cursor: "pointer", opacity: hidden ? 0.7 : 1, transition: "transform .18s, box-shadow .18s, border-color .18s", "&:hover": { transform: "translateY(-3px)", borderColor: "#d9cffb", boxShadow: "0 1px 2px rgba(20,18,30,.05),0 22px 40px -22px rgba(86,40,160,.4)" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "10px", flex: "none", background: "linear-gradient(135deg,#7c3aed,#5b21b6)", color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{initials(b.stationName)}</Box>
        <Box sx={{ fontSize: 15, fontWeight: 800, color: "#16131f", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.stationName}</Box>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "2px", flex: "none" }}>
          <Tooltip title={t(hidden ? "branchCash.unhideStation" : "branchCash.hideStation")}>
            <IconButton size="small" onClick={(e) => toggleHidden(b.stationId, e)} sx={{ color: "#b9b4c4", "&:hover": { color: "#6d28d9" } }}>
              {hidden ? <VisibilityOutlined sx={{ fontSize: 17 }} /> : <VisibilityOffOutlined sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", color: "#6d28d9" }}>
          <AccountBalanceOutlined sx={{ fontSize: 16 }} />
          <Box sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700 }}>{money(toTHB(b), "THB")}</Box>
        </Box>
        <ChevronRightRounded sx={{ fontSize: 20, color: "#b9b4c4", ml: "auto" }} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "10px", mt: "16px" }}>
        {["THB", "LAK", "USD"].map((cur) => (
          <Box key={cur} sx={{ background: "#faf9fc", border: "1px solid #f0eef5", borderRadius: "12px", p: "10px 12px" }}>
            <Box sx={labelSx}>{cur}</Box>
            <Box sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: "#161420", mt: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{money(b[cur], cur)}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1100, mr: "auto", p: { xs: "20px", md: "28px 40px 60px" } }}>
      <Box>
        <Box component="h1" sx={{ m: 0, fontSize: 24, fontWeight: 800, color: "#16131f", letterSpacing: "-.01em" }}>{t("branchCash.title")}</Box>
        <Typography sx={{ fontSize: 13, mt: 0.5 }} color="text.secondary">{t("branchCash.subtitle")}</Typography>
      </Box>

      {/* aggregate KPI tiles across all the user's stations */}
      <Box sx={{ mt: "22px" }}>
        <BranchCashKpis balances={balances} rate={rate} />
      </Box>

      {/* station cards → drill down */}
      <Typography sx={{ mt: "30px", mb: "12px", fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }} color="text.secondary">
        {t("branchCash.stations")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,1fr)" }, gap: "18px" }}>
        {balances.length === 0 ? (
          <Box sx={{ gridColumn: "1 / -1", background: "#fff", border: "1px solid #ece9f1", borderRadius: "20px", p: "40px", textAlign: "center", color: "#a8a3b4" }}>{t("branchCash.noStations")}</Box>
        ) : visibleBalances.length === 0 ? (
          <Box sx={{ gridColumn: "1 / -1", background: "#fff", border: "1px solid #ece9f1", borderRadius: "20px", p: "40px", textAlign: "center", color: "#a8a3b4" }}>{t("branchCash.allStationsHidden")}</Box>
        ) : visibleBalances.map((b) => renderCard(b, false))}
      </Box>

      {/* stations this staff member has hidden from their own list (client-side,
          per-device preference — see loadHiddenStations above) — collapsed by
          default so they stay out of the way, but never fully gone. */}
      {hiddenBalances.length > 0 && (
        <Box sx={{ mt: "26px" }}>
          <Box
            onClick={() => setShowHidden((v) => !v)}
            sx={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none", color: "#8b8697" }}
          >
            <ExpandMoreRounded sx={{ fontSize: 20, transform: showHidden ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>
              {t("branchCash.hiddenStations", { count: hiddenBalances.length })}
            </Typography>
          </Box>
          {showHidden && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,1fr)" }, gap: "18px", mt: "14px" }}>
              {hiddenBalances.map((b) => renderCard(b, true))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
